import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaFolder,
  FaSortAmountDown,
} from 'react-icons/fa';
import BlogTable from '../../../components/admin/BlogTable';
import { apiFetch } from '../../../utils/api';
import Pagination from '../../../components/blog/Pagination';

export default function AdminBlogListPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'Published', 'Draft'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sort, setSort] = useState('created-newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null);
  const blogsPerPage = 8;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/blogs/all');
        if (!res.ok) {
          throw new Error('Failed to fetch blogs');
        }
        const data = await res.json();

        if (isMounted) {
          setBlogs(data);
        }
      } catch (err) {
        console.error(err);
        showToast('Unable to load blogs. Please check backend connection.', 'error');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDelete = async (id) => {
    try {
      const res = await apiFetch(`/api/blogs/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error('Failed to delete blog post');
      }
      setBlogs((prev) => prev.filter((blog) => blog._id !== id));
      showToast('Blog post deleted successfully.');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete blog post.', 'error');
    }
  };

  const handlePublish = async (id) => {
    try {
      const res = await apiFetch(`/api/blogs/${id}/publish`, {
        method: 'PUT',
      });
      if (!res.ok) {
        throw new Error('Failed to publish blog post');
      }
      const updatedBlog = await res.json();
      setBlogs((prev) => prev.map((blog) => (blog._id === id ? updatedBlog : blog)));
      showToast('Blog published successfully!');
    } catch (err) {
      console.error(err);
      showToast('Failed to publish blog post.', 'error');
    }
  };

  const handleUnpublish = async (id) => {
    try {
      const res = await apiFetch(`/api/blogs/${id}/unpublish`, {
        method: 'PUT',
      });
      if (!res.ok) {
        throw new Error('Failed to unpublish blog post');
      }
      const updatedBlog = await res.json();
      setBlogs((prev) => prev.map((blog) => (blog._id === id ? updatedBlog : blog)));
      showToast('Blog moved to drafts.');
    } catch (err) {
      console.error(err);
      showToast('Failed to unpublish blog post.', 'error');
    }
  };

  // Extract unique categories
  const categories = useMemo(() => {
    const set = new Set();
    blogs.forEach((b) => {
      if (b.category) set.add(b.category);
    });
    return Array.from(set);
  }, [blogs]);

  // Summary Metrics
  const stats = useMemo(() => {
    const total = blogs.length;
    const published = blogs.filter((b) => b.status === 'Published').length;
    const drafts = blogs.filter((b) => b.status === 'Draft').length;
    return { total, published, drafts, categoryCount: categories.length };
  }, [blogs, categories]);

  // Filter & Sort
  const filteredBlogs = useMemo(() => {
    return blogs
      .filter((blog) => {
        if (filter === 'all') return true;
        return blog.status?.toLowerCase() === filter.toLowerCase();
      })
      .filter((blog) => {
        if (selectedCategory === 'all') return true;
        return blog.category === selectedCategory;
      })
      .filter((blog) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
          blog.title?.toLowerCase().includes(q) ||
          blog.authorName?.toLowerCase().includes(q) ||
          blog.category?.toLowerCase().includes(q) ||
          blog.tags?.some((t) => t.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => {
        if (sort === 'created-newest') return new Date(b.createdAt) - new Date(a.createdAt);
        if (sort === 'created-oldest') return new Date(a.createdAt) - new Date(b.createdAt);
        if (sort === 'title-az') return (a.title || '').localeCompare(b.title || '');
        if (sort === 'title-za') return (b.title || '').localeCompare(a.title || '');
        return 0;
      });
  }, [blogs, filter, selectedCategory, searchTerm, sort]);

  // Pagination
  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    return filteredBlogs.slice(startIndex, startIndex + blogsPerPage);
  }, [filteredBlogs, currentPage, blogsPerPage]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl px-5 py-3.5 text-sm font-semibold shadow-xl transition-all ${
            toast.type === 'error'
              ? 'border border-red-200 bg-red-600 text-white'
              : 'border border-emerald-200 bg-slate-900 text-white'
          }`}
        >
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Header Bar */}
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Content Studio</p>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Blog Management</h1>
          <p className="text-sm text-slate-500">Create, manage, format and optimize your corporate publications.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <Link
            to="/admin/categories"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50 hover:text-slate-900"
          >
            <FaFolder className="text-amber-500 text-xs sm:text-sm" /> Manage Categories
          </Link>
          <Link
            to="/admin/blogs/create"
            className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 text-xs sm:text-sm font-bold text-slate-950 shadow-xs transition hover:bg-amber-300"
          >
            <FaPlus className="text-xs" /> Create Blog
          </Link>
        </div>
      </div>

      {/* Top Stat Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Articles</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <FaFileAlt className="text-lg" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Published</p>
              <p className="mt-1 text-2xl font-bold text-emerald-600">{stats.published}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <FaCheckCircle className="text-lg" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Drafts</p>
              <p className="mt-1 text-2xl font-bold text-amber-600">{stats.drafts}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <FaClock className="text-lg" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Categories</p>
              <p className="mt-1 text-2xl font-bold text-sky-600">{stats.categoryCount}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
              <FaFolder className="text-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => {
                setFilter('all');
                setCurrentPage(1);
              }}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${
                filter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              type="button"
              onClick={() => {
                setFilter('Published');
                setCurrentPage(1);
              }}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${
                filter === 'Published' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Published ({stats.published})
            </button>
            <button
              type="button"
              onClick={() => {
                setFilter('Draft');
                setCurrentPage(1);
              }}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition ${
                filter === 'Draft' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Drafts ({stats.drafts})
            </button>
          </div>

          {/* Search & Select dropdowns */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <input
                type="text"
                placeholder="Search blogs, authors, tags..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-8 text-xs text-slate-800 transition placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:outline-none"
              />
              <FaSearch className="absolute left-3 top-2.5 text-xs text-slate-400" />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-700"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            {/* Category Dropdown */}
            {categories.length > 0 && (
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 transition focus:border-amber-400 focus:bg-white focus:outline-none"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            )}

            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 py-2 pl-8 pr-4 text-xs font-medium text-slate-700 transition focus:border-amber-400 focus:bg-white focus:outline-none"
              >
                <option value="created-newest">Newest First</option>
                <option value="created-oldest">Oldest First</option>
                <option value="title-az">Title A-Z</option>
                <option value="title-za">Title Z-A</option>
              </select>
              <FaSortAmountDown className="absolute left-2.5 top-2.5 text-xs text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Blog Table Section */}
      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-12">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
            <p className="text-sm font-medium text-slate-500">Loading blog articles...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <BlogTable
            blogs={paginatedBlogs}
            onDelete={handleDelete}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
          />

          {filteredBlogs.length > blogsPerPage && (
            <div className="flex justify-center pt-2">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredBlogs.length / blogsPerPage)}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
